import type { FC, PropsWithChildren } from 'react';
import { useContext, useMemo } from 'react';

import type { Filters } from '@cognite/fdm-client';

import { AppConfigFiltersContext } from './app-config-filters-context';
import { CdmAppConfigFiltersProviderContext } from './cdm-app-config-filters-provider.context';
import type { ConfigFiltersContextType } from './types';
import { notArchivedFilter } from './utils/constants';
import { createRootLocationFilter } from './utils/create-root-location-filter';

const emptyDmFilters: Filters = { and: [] };

export const CdmAppConfigFiltersProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { useCdmSelectedLocationContext } = useContext(
    CdmAppConfigFiltersProviderContext
  );

  const { selectedLocationConfig } = useCdmSelectedLocationContext();

  const context: ConfigFiltersContextType = useMemo(() => {
    const generalRootLocationFilter = createRootLocationFilter(
      selectedLocationConfig?.dataStorage?.rootLocation
    );

    return {
      template: { rootAssetExternalIds: generalRootLocationFilter },
      checklist: { rootAssetExternalIds: generalRootLocationFilter },
      observation: { rootAssetExternalIds: generalRootLocationFilter },
      timeseries: undefined,
      asset: {
        cdfClassicFilters: {
          assetSubtreeIds: undefined,
          externalIdPrefix: undefined,
        },
        dmFilters: emptyDmFilters,
      },
      files: undefined,
      activity: {},
      notArchivedFilter,
    };
  }, [selectedLocationConfig?.dataStorage?.rootLocation]);

  return (
    <AppConfigFiltersContext.Provider value={context}>
      {children}
    </AppConfigFiltersContext.Provider>
  );
};

export const useAppConfigFiltersContext = () => {
  const context = useContext(AppConfigFiltersContext);
  if (!context) {
    throw new Error(
      'AppConfigFiltersContext must be used within a AppConfigFiltersProvider'
    );
  }
  return context;
};
